import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inquiries`).then((r) => setInquiries(r.data)).catch(() => {}),
      axios.get(`${API}/analytics/summary`).then((r) => setStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-navy text-white p-6 md:p-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <img src="/logo-mark.png" alt="logo" className="h-12 w-auto" />
          <h1 className="font-serif-lux text-3xl">Dashboard</h1>
        </div>

        {loading ? (
          <p className="text-white/60">Loading…</p>
        ) : (
          <>
            <Analytics stats={stats} />

            <div className="flex items-center justify-between mt-16 mb-6">
              <h2 className="font-serif-lux text-2xl text-gold-light">Inquiries</h2>
              <span className="text-white/50 text-sm">{inquiries.length} total</span>
            </div>
            {inquiries.length === 0 ? (
              <p className="text-white/60" data-testid="admin-empty">No inquiries yet.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {inquiries.map((iq) => (
                  <div key={iq.id} data-testid={`admin-inquiry-${iq.id}`} className="border border-gold/20 p-6 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => setActive(active === iq.id ? null : iq.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-serif-lux text-xl text-gold-light">{iq.name}</h3>
                        <p className="text-white/70 text-sm">{iq.email}</p>
                      </div>
                      <span className="text-white/40 text-xs">{iq.created_at ? format(new Date(iq.created_at), "PP") : ""}</span>
                    </div>
                    <div className="gold-line w-full my-4 opacity-30" />
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <Field l="Phone" v={iq.phone} /><Field l="Guests" v={iq.guests} />
                      <Field l="Dates" v={iq.travel_dates} /><Field l="Budget" v={iq.budget} />
                      <Field l="Destinations" v={iq.destinations} full /><Field l="Interests" v={iq.interests} full />
                      {active === iq.id && <Field l="Special Requests" v={iq.special_requests} full />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const Analytics = ({ stats }) => {
  if (!stats) return null;
  const eventMap = Object.fromEntries((stats.by_event || []).map((e) => [e.label, e.count]));
  return (
    <div data-testid="analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
        <Stat label="Total Views" value={stats.total_views} testid="stat-views" />
        <Stat label="Countries" value={(stats.by_country || []).length} />
        <Stat label="Language Switches" value={eventMap["language_toggle"] || 0} />
        <Stat label="Inquiries Sent" value={eventMap["inquiry_submit"] || 0} />
      </div>

      <TrafficChart data={stats.timeseries || []} />

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        <RankList title="Top Countries" rows={stats.by_country} testid="list-countries" />
        <RankList title="Top Cities" rows={stats.by_city} isCity testid="list-cities" />
      </div>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="border border-gold/20 p-6" data-testid={testid}>
    <div className="font-serif-lux text-gold-light text-4xl">{value}</div>
    <div className="text-white/50 text-xs tracking-luxe uppercase mt-2">{label}</div>
  </div>
);

const TrafficChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  const MAX_PX = 190;
  return (
    <div className="border border-gold/20 p-6" data-testid="traffic-chart">
      <h3 className="text-gold text-xs tracking-luxe uppercase mb-6">Traffic Over Time</h3>
      {data.length === 0 ? (
        <p className="text-white/50 text-sm">No traffic recorded yet.</p>
      ) : (
        <div className="flex items-end gap-2 h-56 overflow-x-auto">
          {data.map((d) => (
            <div key={d.date} className="flex flex-col items-center justify-end flex-1 min-w-[26px] max-w-[80px] h-full group">
              <span className="text-gold-light text-xs mb-1 opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>
              <div
                className="w-full bg-gold/70 hover:bg-gold transition-colors"
                style={{ height: `${Math.round((d.count / max) * MAX_PX)}px`, minHeight: "4px" }}
                title={`${d.date}: ${d.count}`}
              />
              <span className="text-white/40 text-[10px] mt-2 whitespace-nowrap">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RankList = ({ title, rows = [], isCity, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-gold/20 p-6" data-testid={testid}>
      <h3 className="text-gold text-xs tracking-luxe uppercase mb-6">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-white/50 text-sm">No data yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white/80">
                  {r.label}
                  {isCity && r.country ? <span className="text-white/40"> · {r.country}</span> : null}
                </span>
                <span className="text-gold-light">{r.count}</span>
              </div>
              <div className="h-1 bg-white/10">
                <div className="h-full bg-gold/70" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ l, v, full }) =>
  v ? (
    <div className={full ? "col-span-2" : ""}>
      <span className="text-gold/70 text-xs tracking-widest uppercase">{l}: </span>
      <span className="text-white/80">{v}</span>
    </div>
  ) : null;
